import * as zod from 'zod';

import {
  BlendConfig,
  MotionKind,
  MotionChildTCPOption,
} from '@sb/motion-planning';
import {
  ActionRequiredError,
  DistanceUnit,
  MotionSpeed,
  SpeedLimitOption,
  Step,
} from '@sb/remote-control/types';
import type { WaypointStepArguments } from '@sb/routine-runner';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import { Expression, Space } from '@sb/routine-runner/types';

import { calculateSpeedProfileFromStepMotionSpeed } from '../util/calculateSpeedProfileFromStepMotionSpeed';
import { buildConditionalFromJSON } from '../util/conditionalsBuilder';
import { getDistanceUnitInfo } from '../util/distance';
import convertJavaScriptExpressionToUser from '../util/expressions/convertJavaScriptExpressionToUser';
import { convertPythonExpression } from '../util/expressions/v2';
import { listAllSteps } from '../util/listAllSteps';
import { validateMotionSpeed } from '../util/validators';

export namespace WaypointStep {
  export const name = 'Waypoint';
  export const description = 'Move the robot to a specific position in space';
  export const librarySection = Step.LibrarySection.Hidden;
  export const librarySort = '1'; // unused
  export const argumentKind = 'Waypoint';
  export const featureFlag = 'ros2';

  /**
   * Get the dynamic name for a waypoint step based on its configuration
   */
  export const getDynamicName = (args?: Arguments): string => {
    if (args?.targetKind === 'relative') {
      return 'Relative Waypoint';
    }

    return name;
  };

  export const Arguments = zod.object({
    argumentKind: zod.literal('Waypoint'),
    shouldMatchJointAngles: zod.boolean().default(false),
    shouldMatchDynamicBasePosition: zod.boolean().default(false),
    motionKind: MotionKind.default('joint'),
    // The setting `motionKind` is derived from. Never `.default()`: a waypoint
    // written before the twin existed, or by a writer that sends only the
    // effective field, has none, and a default would read as the operator
    // choosing joint motion. Read it through `rawOrEffective`.
    motionKindRaw: MotionKind.optional(),
    motionSpeed: zod.preprocess(
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    speedLimitOption: SpeedLimitOption.default('PARENT_DEFAULTS'),
    positionListID: zod.string().nullable().default(null),
    positionListIndex: zod.number().optional(),
    target: Space.Position.nullable().default(null),
    tcpOption: MotionChildTCPOption.default('auto'),
    gripperID: zod.string().optional(),
    expression: Expression.optional(),
    targetKind: zod
      .union([
        zod.literal('singlePosition'),
        zod.literal('positionList'),
        zod.literal('expression'),
        zod.literal('relative'),
      ])
      .default('singlePosition'),
    stopHere: zod.boolean().default(false),
    // Same rule as `motionKindRaw`.
    stopHereRaw: zod.boolean().optional(),
    blendConfig: BlendConfig.default({ kind: 'blendRadius', radius: 0 }),
    useParentBlendConfig: zod.boolean().default(true),
    moveDynamicBaseToReachPosition: zod.boolean().default(true),
    distanceUnit: DistanceUnit.default('meter'),
    palletConfig: zod
      .object({
        selectedLayer: Expression.optional(),
        selectedBox: Expression.optional(),
        selectedBoxType: zod.string().optional(),
        selectedPalletBaseID: zod.string().optional(),
        moveType: zod
          .union([
            zod.literal('box'),
            zod.literal('approach'),
            zod.literal('pickup'),
          ])
          .default('box'),
      })
      .default({
        selectedLayer: undefined,
        selectedBox: undefined,
        selectedBoxType: undefined,
        selectedPalletBaseID: undefined,
        moveType: 'box',
      }),
    relativeConfig: zod
      .object({
        targetKind: zod
          .union([
            zod.literal('singlePosition'),
            zod.literal('positionList'),
            zod.literal('expression'),
          ])
          .default('singlePosition'),
        frame: zod.string().default('base'),
        offsetX: Expression.optional(),
        offsetY: Expression.optional(),
        offsetZ: Expression.optional(),
        offsetRx: Expression.optional(),
        offsetRy: Expression.optional(),
        offsetRz: Expression.optional(),
      })
      .default({
        targetKind: 'singlePosition',
        frame: 'base',
        offsetX: undefined,
        offsetY: undefined,
        offsetZ: undefined,
        offsetRx: undefined,
        offsetRy: undefined,
        offsetRz: undefined,
      }),
    stopEarlyConditions: zod.string().default(''),
    useDeterministicPlanner: zod.boolean().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  /**
   * The `positionListID` this waypoint hands the runner, or `null` when it
   * hands none. Only the target kinds that reference a position carry one, so
   * an id left behind by a kind switch never reaches the runner — and nothing
   * reading the waypoint's effective target may read it either. `getTarget`
   * below is the only writer, and calls this so the two cannot drift.
   */
  export const emittedPositionListID = ({
    targetKind,
    positionListID,
  }: Pick<Arguments, 'targetKind' | 'positionListID'>): string | null =>
    targetKind === 'positionList' || targetKind === 'relative'
      ? positionListID
      : null;

  /**
   * The joint-matching flag this waypoint hands the runner. A relative target
   * carries its pose from wherever the arm already is, so there are no stored
   * joint angles to match and the flag is dropped on the way out — anything
   * judging the waypoint's effective behaviour has to drop it the same way.
   */
  export const emittedJointMatching = ({
    targetKind,
    shouldMatchJointAngles,
  }: Pick<Arguments, 'targetKind' | 'shouldMatchJointAngles'>): boolean =>
    targetKind === 'relative' ? false : shouldMatchJointAngles;

  /**
   * The dynamic-base matching flag this waypoint hands the runner, dropped for
   * a relative target for the same reason as `emittedJointMatching`. It decides
   * whether a joint-matched waypoint resolves a tooltip at all: the dynamic-base
   * pass re-runs the target with pose generation forced, which is the one path
   * that reaches tooltip resolution through joint matching.
   */
  export const emittedDynamicBaseMatching = ({
    targetKind,
    shouldMatchDynamicBasePosition,
  }: Pick<
    Arguments,
    'targetKind' | 'shouldMatchDynamicBasePosition'
  >): boolean =>
    targetKind === 'relative' ? false : shouldMatchDynamicBasePosition;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      target,
      expression,
      targetKind,
      shouldMatchJointAngles,
      shouldMatchDynamicBasePosition,
      motionKind,
      motionSpeed,
      speedLimitOption,
      positionListID,
      positionListIndex,
      stopHere,
      blendConfig,
      useParentBlendConfig,
      tcpOption,
      gripperID,
      distanceUnit,
      relativeConfig,
      palletConfig,
      stopEarlyConditions,
      useDeterministicPlanner,
    } = args;

    validateMotionSpeed(name, motionSpeed);

    const speedProfile: SpeedProfile = calculateSpeedProfileFromStepMotionSpeed(
      {
        motionSpeed,
        baseSpeedProfile,
      },
    );

    const getTarget = (): WaypointStepArguments['target'] => {
      const emittedID = emittedPositionListID({ targetKind, positionListID });

      switch (targetKind) {
        case 'positionList':
          if (emittedID) {
            return {
              positionListID: emittedID,
              positionListIndex,
            };
          }

          break;

        case 'expression':
          if (expression) {
            return { expression };
          }

          break;

        case 'relative':
          if (emittedID) {
            const targetReturn: any = {
              positionListID: emittedID,
              positionListIndex,
            };

            // If this is a relative waypoint with expression, include the expression
            if (emittedID === 'expression' && expression) {
              targetReturn.expression = expression;
            }

            return targetReturn;
          }

          break;

        default:
          if (target) {
            return target;
          }
      }

      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target is not configured',
      });
    };

    // Cannot blend and stop at the same time - if stopping, don't use parent blend config
    const effectiveUseParentBlendConfig = stopHere
      ? false
      : useParentBlendConfig;

    const effectiveShouldMatchJointAngles = emittedJointMatching({
      targetKind,
      shouldMatchJointAngles,
    });

    const effectiveShouldMatchDynamicBasePosition = emittedDynamicBaseMatching({
      targetKind,
      shouldMatchDynamicBasePosition,
    });

    // Only send relativeConfig when targetKind is 'relative', otherwise send nulled version
    const effectiveRelativeConfig =
      targetKind === 'relative'
        ? {
            ...relativeConfig,
            // Convert expressions to the format expected by routine-runner
            offsetX: relativeConfig.offsetX,
            offsetY: relativeConfig.offsetY,
            offsetZ: relativeConfig.offsetZ,
            offsetRx: relativeConfig.offsetRx,
            offsetRy: relativeConfig.offsetRy,
            offsetRz: relativeConfig.offsetRz,
          }
        : {
            targetKind: 'singlePosition' as const,
            frame: 'base',
            offsetX: undefined,
            offsetY: undefined,
            offsetZ: undefined,
            offsetRx: undefined,
            offsetRy: undefined,
            offsetRz: undefined,
          };

    const result = {
      ...stepData,
      stepKind: 'Waypoint' as const,
      args: {
        target: getTarget(),
        shouldMatchJointAngles: effectiveShouldMatchJointAngles,
        shouldMatchDynamicBasePosition: effectiveShouldMatchDynamicBasePosition,
        motionKind,
        stopHere,
        blendConfig,
        useParentBlendConfig: effectiveUseParentBlendConfig,
        motionSpeedPercent: motionSpeed?.motionSpeedPercent ?? null,
        speedProfile,
        useParentSpeedProfile: speedLimitOption === 'PARENT_DEFAULTS',
        tcpOption,
        gripperID,
        distanceUnit,
        relativeConfig: effectiveRelativeConfig,
        palletConfig,
        stopEarlyConditions: stopEarlyConditions
          ? buildConditionalFromJSON(stopEarlyConditions)
          : null,
        useDeterministicPlanner,
      },
    };

    return result;
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    routine,
    includeStepName,
    globalSpace,
  }) => {
    if (args?.argumentKind !== argumentKind || !routine) {
      return null;
    }

    // Helper function to prefix step name if required
    const prefixStepName = (description_: string) =>
      includeStepName ? `${name} ${description_}` : description_;

    if (args.targetKind === 'singlePosition') {
      // arbitrary specified point
      return prefixStepName('to manually specified position');
    }

    const allSpace = [...(globalSpace ?? []), ...routine.space];

    if (args.targetKind === 'positionList') {
      const space = allSpace.find((item) => item.id === args.positionListID);

      if (!space) {
        return null;
      }

      if (space.kind === 'singlePosition') {
        return prefixStepName(`to ${space.name}`);
      }

      if (space.kind === 'safeHomePose') {
        return prefixStepName('to home pose');
      }

      // Pallet items
      if (space.kind === 'palletBoxes') {
        const moveType = args.palletConfig?.moveType ?? 'box';

        if (moveType === 'approach') {
          return prefixStepName(
            `moving to approach positions of ${space.name}`,
          );
        }

        if (moveType === 'box') {
          return prefixStepName(`moving to box positions of ${space.name}`);
        }

        if (moveType === 'pickup') {
          const selectedBoxTypeID = args.palletConfig?.selectedBoxType;

          if (!selectedBoxTypeID) {
            return prefixStepName(`pickup position of ${space.name}`);
          }

          const boxType = selectedBoxTypeID
            ? space.boxTypes.find((bt: any) => bt.id === selectedBoxTypeID)
            : undefined;

          const boxTypeName =
            boxType?.name ?? selectedBoxTypeID ?? 'selected box type';

          const pickupPositionName =
            boxType?.pickupPosition?.name ?? 'pickup position';

          return prefixStepName(
            `moving to ${pickupPositionName} of box type ${boxTypeName} of ${space.name}`,
          );
        }
      }

      if (
        space.kind === 'freeformPositionList' ||
        space.kind === 'gridPositionList'
      ) {
        if (args.positionListIndex == null) {
          return prefixStepName(`to the next position in ${space.name}`);
        }

        return prefixStepName(
          `to position ${args.positionListIndex + 1} in ${space.name}`,
        );
      }

      return null;
    }

    if (args.targetKind === 'expression') {
      if (args.expression == null) {
        return null;
      }

      if (args.expression.kind === 'JavaScript') {
        // This looks pretty gnarly for most expressions. Probably want to allow
        // the user to build up the expression using a DSL that we can desugar
        // into JS later.
        const jsExprStr = convertJavaScriptExpressionToUser(
          args.expression.expression,
          { isForStepDescription: true },
        );

        return prefixStepName(`based on expression ${jsExprStr}`);
      }

      if (args.expression.kind === 'Python') {
        const { code: pyExprStr } =
          convertPythonExpression.withRemoteControlVariables.toUserDescription(
            args.expression.expression,
          );

        return prefixStepName(`based on expression ${pyExprStr}`);
      }

      return null;
    }

    if (args.targetKind === 'relative') {
      // Get the target description based on the positionListID
      let targetDescription: string | null = null;

      // For relative waypoints, the target is determined by positionListID
      if (args.positionListID === 'expression') {
        // Expression target
        if (args.expression) {
          if (args.expression.kind === 'JavaScript') {
            const jsExprStr = convertJavaScriptExpressionToUser(
              args.expression.expression,
              { isForStepDescription: true },
            );

            targetDescription = `expression ${jsExprStr}`;
          } else if (args.expression.kind === 'Python') {
            const { code: pyExprStr } =
              convertPythonExpression.withRemoteControlVariables.toUserDescription(
                args.expression.expression,
              );

            targetDescription = `expression ${pyExprStr}`;
          }
        }
      } else if (args.positionListID === 'current-position') {
        // Current position target
        targetDescription = 'current position';
      } else if (args.positionListID) {
        // Check if it's a waypoint reference
        const allSteps = listAllSteps(routine.steps);

        const waypointStep = allSteps.find(
          (step) => step.id === args.positionListID,
        );

        if (waypointStep && waypointStep.stepKind === 'Waypoint') {
          // Get a simple description for the waypoint without causing circular dependency
          const waypointConfig = routine.stepConfigurations[waypointStep.id];
          let waypointDescription = '';

          if (
            waypointConfig?.args &&
            waypointConfig.args.argumentKind === 'Waypoint'
          ) {
            const waypointArgs = waypointConfig.args as WaypointStep.Arguments;

            if (waypointArgs.targetKind === 'singlePosition') {
              waypointDescription = 'manually specified position';
            } else if (
              waypointArgs.targetKind === 'positionList' &&
              waypointArgs.positionListID
            ) {
              const waypointSpace = allSpace.find(
                (item) => item.id === waypointArgs.positionListID,
              );

              if (waypointSpace) {
                if (waypointSpace.kind === 'singlePosition') {
                  waypointDescription = waypointSpace.name;
                } else if (waypointSpace.kind === 'safeHomePose') {
                  waypointDescription = 'home pose';
                } else if (
                  waypointSpace.kind === 'freeformPositionList' ||
                  waypointSpace.kind === 'gridPositionList'
                ) {
                  waypointDescription = waypointSpace.name;
                }
              }
            } else if (
              waypointArgs.targetKind === 'expression' &&
              waypointArgs.expression
            ) {
              waypointDescription = 'expression';
            }
          }

          targetDescription = `waypoint ${waypointStep.stepPosition}${waypointDescription ? ` (${waypointDescription})` : ''}`;
        } else {
          // Position list target (space item)
          const space = allSpace.find(
            (item) => item.id === args.positionListID,
          );

          if (space) {
            if (space.kind === 'singlePosition') {
              targetDescription = space.name;
            } else if (space.kind === 'safeHomePose') {
              targetDescription = 'home pose';
            } else if (
              space.kind === 'freeformPositionList' ||
              space.kind === 'gridPositionList'
            ) {
              if (args.positionListIndex == null) {
                targetDescription = `the next position in ${space.name}`;
              } else {
                targetDescription = `position ${args.positionListIndex + 1} in ${space.name}`;
              }
            }
          }
        }
      }

      if (!targetDescription) {
        return null;
      }

      // Check if there are any offsets
      const { offsetX, offsetY, offsetZ, offsetRx, offsetRy, offsetRz } =
        args.relativeConfig;

      const hasOffsets =
        offsetX != null ||
        offsetY != null ||
        offsetZ != null ||
        offsetRx != null ||
        offsetRy != null ||
        offsetRz != null;

      if (!hasOffsets) {
        return prefixStepName(`to ${targetDescription}`);
      }

      // Format the offsets
      const offsetParts: string[] = [];

      // Use the distanceUnit from the configuration
      const unitInfo = getDistanceUnitInfo(args.distanceUnit);

      // Helper function to format expression offsets
      const formatExpressionOffset = (
        expr?: Expression,
        axis?: string,
        isRotation = false,
      ) => {
        if (expr == null) return null;

        // only handle JS+python expressions for now
        if (expr.kind !== 'JavaScript' && expr.kind !== 'Python') {
          return null;
        }

        // skip offsets of zero
        const value = parseFloat(expr.expression);
        if (value === 0) return null;

        let displayExpression = expr.expression;

        // Convert variable references to user-friendly names
        if (expr.kind === 'JavaScript') {
          displayExpression = convertJavaScriptExpressionToUser(
            expr.expression,
            { isForStepDescription: true },
          );
        } else if (expr.kind === 'Python') {
          const { code } =
            convertPythonExpression.withRemoteControlVariables.toUserDescription(
              expr.expression,
            );

          displayExpression = code;
        }

        if (isRotation) {
          return `${displayExpression} deg in ${axis}`;
        }

        return `${displayExpression} ${unitInfo.abbreviation} in ${axis}`;
      };

      // Add translation offsets
      const translationParts = [
        formatExpressionOffset(offsetX, 'X'),
        formatExpressionOffset(offsetY, 'Y'),
        formatExpressionOffset(offsetZ, 'Z'),
      ].filter((p): p is string => p != null);

      if (translationParts.length > 0) {
        offsetParts.push(...translationParts);
      }

      // Add rotation offsets
      const rotationParts = [
        formatExpressionOffset(offsetRx, 'Roll', true),
        formatExpressionOffset(offsetRy, 'Pitch', true),
        formatExpressionOffset(offsetRz, 'Yaw', true),
      ].filter((p): p is string => p != null);

      if (rotationParts.length > 0) {
        offsetParts.push(...rotationParts);
      }

      // Format with Oxford comma
      let offsetDescription: string;

      if (offsetParts.length === 0) {
        offsetDescription = '';
      } else if (offsetParts.length === 1) {
        [offsetDescription] = offsetParts;
      } else if (offsetParts.length === 2) {
        offsetDescription = `${offsetParts[0]} and ${offsetParts[1]}`;
      } else {
        const lastPart = offsetParts.pop();
        offsetDescription = `${offsetParts.join(', ')}, and ${lastPart}`;
      }

      return prefixStepName(
        `of ${offsetDescription} from ${targetDescription}`,
      );
    }

    return null;
  };
}

WaypointStep satisfies Step.StepKindInfo;
